import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material'
import {
  add_telegram_user,
  edit_user_permissions,
  get_telegram_users,
  toggle_user_activation,
} from 'api/clothes_service'
import { UserContext } from 'base'
import BasicDialog from 'components/basic_dialog'
import { TelegramUserEditForm } from 'components/telegram_user_edit_form'
import { TelegramUserForm } from 'components/telegram_user_form'
import UserCard from 'components/user_card'
import { UnauthorisedError } from 'errors'
import { useDocumentTitle } from 'hooks'
import { TelegramUser, UserPermission } from 'models'
import { useCallback, useContext, useEffect, useState } from 'react'

interface UsersContentProps {
  telegramUsers: Array<TelegramUser> | undefined
  error: string | undefined
  handleToggleActivation: (
    telegramUserId: string,
    shouldActivate: boolean
  ) => Promise<void>
  handleEdit: (telegramUser: TelegramUser) => void
}

const UsersContent = ({
  telegramUsers,
  error,
  handleToggleActivation,
  handleEdit,
}: UsersContentProps) => {
  if (error !== undefined) {
    return (
      <Alert severity="error">
        <Typography variant="h5">{error}</Typography>
      </Alert>
    )
  }

  if (telegramUsers === undefined) {
    return (
      <Box display="flex" justifyContent="center" pt={2}>
        <Stack display="flex" alignItems="center" spacing={1}>
          <CircularProgress />
          <Typography variant="body1">Loading data...</Typography>
        </Stack>
      </Box>
    )
  }

  if (telegramUsers.length === 0) {
    return <Typography>No users.</Typography>
  }

  return telegramUsers.map((telegramUser) => {
    return (
      <UserCard
        telegramUser={telegramUser}
        handleToggleActivation={handleToggleActivation}
        handleEdit={handleEdit}
      />
    )
  })
}

const Users = () => {
  const user = useContext(UserContext)

  const [telegramUsers, setTelegramUsers] = useState<Array<TelegramUser>>()
  const [selectedTelegramUser, setSelectedTelegramUser] =
    useState<TelegramUser>(new TelegramUser())
  const [error, setError] = useState<string>()
  const [isAddDialogOpen, setIsAddDialogOpen] = useState<boolean>(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState<boolean>(false)
  const [showSnackbarError, setShowSnackbarError] = useState<boolean>(false)
  const [snackbarError, setSnackbarError] = useState<string>('')

  const populateTelegramUsers = useCallback(async () => {
    setError(undefined)
    try {
      const telegramUsers = await get_telegram_users()
      setTelegramUsers(telegramUsers)
    } catch (e: any) {
      if (e instanceof UnauthorisedError) {
        return user.setLoggedOut()
      }
      setError(e.message)
    }
  }, [user])

  useEffect(() => {
    populateTelegramUsers()
  }, [populateTelegramUsers])

  const handleToggleActivation = useCallback(
    async (telegramUserId: string, shouldActivate: boolean) => {
      try {
        await toggle_user_activation(telegramUserId, shouldActivate)
      } catch (e: any) {
        if (e instanceof UnauthorisedError) {
          return user.setLoggedOut()
        }
        setSnackbarError(e.message)
        setShowSnackbarError(true)
        return
      }
      populateTelegramUsers()
    },
    [user, populateTelegramUsers]
  )

  const handleEdit = (telegramUser: TelegramUser) => {
    setSelectedTelegramUser(telegramUser)
    setIsEditDialogOpen(true)
  }

  const handleSnackbarClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === 'clickaway') {
      return
    }
    setShowSnackbarError(false)
  }

  useDocumentTitle('Telegram Users')
  return (
    <>
      <Stack spacing={2}>
        <Typography variant="h3">{'Telegram Users'}</Typography>
        <Box display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            color="primary"
            onClick={() => setIsAddDialogOpen(true)}
          >
            Add User
          </Button>
        </Box>
        <UsersContent
          telegramUsers={telegramUsers}
          error={error}
          handleToggleActivation={handleToggleActivation}
          handleEdit={handleEdit}
        />
      </Stack>

      <BasicDialog
        open={isAddDialogOpen}
        onClose={() => setIsAddDialogOpen(false)}
        title="Add Telegram User"
      >
        <TelegramUserForm
          onSubmit={async (
            userName: string,
            userFirstName: string,
            userTelegramUsername: string,
            selectedPermissions: Array<UserPermission>
          ) => {
            try {
              await add_telegram_user(
                userName,
                userFirstName,
                userTelegramUsername,
                selectedPermissions
              )
            } catch (e: any) {
              if (e instanceof UnauthorisedError) {
                return user.setLoggedOut()
              }
              setSnackbarError(e.message)
              setShowSnackbarError(true)
              return
            }
            setIsAddDialogOpen(false)
            populateTelegramUsers()
          }}
        ></TelegramUserForm>
      </BasicDialog>

      <BasicDialog
        open={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        title={`Edit Permissions for ${selectedTelegramUser.name}`}
      >
        <TelegramUserEditForm
          existingPermissions={selectedTelegramUser.permissions}
          onSubmit={async (selectedPermissions: Array<UserPermission>) => {
            try {
              await edit_user_permissions(
                selectedTelegramUser._id,
                selectedPermissions
              )
            } catch (e: any) {
              if (e instanceof UnauthorisedError) {
                return user.setLoggedOut()
              }
              setSnackbarError(e.message)
              setShowSnackbarError(true)
              return
            }
            setIsEditDialogOpen(false)
            populateTelegramUsers()
          }}
        ></TelegramUserEditForm>
      </BasicDialog>

      <Snackbar
        open={showSnackbarError}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        onClose={handleSnackbarClose}
      >
        <Alert
          elevation={3}
          onClose={handleSnackbarClose}
          severity="error"
          sx={{ width: '100%' }}
        >
          <Typography variant="h5">{snackbarError}</Typography>
        </Alert>
      </Snackbar>
    </>
  )
}

export default Users
